import React from 'react';
import { Box, Skeleton, Paper } from '@mui/material';
import MDBox from 'components/MDBox';

/**
 * LoadingSkeleton - Reusable loading skeleton component for admin screens
 * 
 * @param {Object} props
 * @param {string} props.variant - 'table' | 'grid' | 'list' | 'card' | 'custom'
 * @param {number} props.rows - Number of skeleton rows (default: 5)
 * @param {boolean} props.showHeader - Show header skeleton (default: true)
 * @param {boolean} props.showStats - Show stats skeleton (default: false)
 * @param {Object} props.sx - Additional styles
 */
const LoadingSkeleton = ({ 
  variant = 'table', 
  rows = 5, 
  showHeader = true,
  showStats = false,
  sx = {} 
}) => {
  const renderTableSkeleton = () => (
    <Box sx={{ width: '100%', ...sx }}>
      {showHeader && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={100} height={40} sx={{ borderRadius: 1 }} />
        </Box>
      )}
      {showStats && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, p: 1.5, borderRadius: 1 }}>
          <Skeleton variant="text" width={80} height={24} />
          <Skeleton variant="text" width={80} height={24} />
          <Skeleton variant="text" width={80} height={24} />
        </Box>
      )}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        {/* Table header */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
          <Skeleton variant="rectangular" width={40} height={32} />
          <Skeleton variant="rectangular" width={120} height={32} />
          <Skeleton variant="rectangular" width={150} height={32} />
          <Skeleton variant="rectangular" width={120} height={32} />
          <Skeleton variant="rectangular" width={100} height={32} />
          <Skeleton variant="rectangular" width={120} height={32} />
        </Box>
        {/* Table rows */}
        {Array.from({ length: rows }).map((_, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 0.5 }} />
            <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 0.5 }} />
            <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 0.5 }} />
            <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 0.5 }} />
            <Skeleton variant="rectangular" width={100} height={40} sx={{ borderRadius: 0.5 }} />
            <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 0.5 }} />
          </Box>
        ))}
      </Paper>
    </Box>
  );

  const renderGridSkeleton = () => (
    <Box sx={{ width: '100%', ...sx }}>
      {showHeader && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2 }}>
        {Array.from({ length: rows }).map((_, index) => (
          <Paper key={index} elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Skeleton variant="circular" width={40} height={40} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="text" width="90%" height={20} />
          </Paper>
        ))}
      </Box>
    </Box>
  );

  const renderListSkeleton = () => (
    <Box sx={{ width: '100%', ...sx }}>
      {Array.from({ length: rows }).map((_, index) => (
        <Paper key={index} elevation={0} sx={{ p: 2, mb: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Skeleton variant="circular" width={48} height={48} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="60%" height={20} />
            </Box>
            <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 0.5 }} />
          </Box>
        </Paper>
      ))}
    </Box>
  );

  const renderCardSkeleton = () => (
    <Box sx={{ width: '100%', ...sx }}>
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 0.5 }} />
          <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 0.5 }} />
        </Box>
      </Paper>
    </Box>
  );

  switch (variant) {
    case 'table':
      return renderTableSkeleton();
    case 'grid':
      return renderGridSkeleton();
    case 'list':
      return renderListSkeleton();
    case 'card':
      return renderCardSkeleton();
    default:
      return renderTableSkeleton();
  }
};

export default LoadingSkeleton;

